import * as React from "react";
import {FormattedMessage, injectIntl} from "react-intl";
import {Link} from "react-router-dom";
import {ColumnsDescription, ColumnWidth} from "../common/ui/table/ControlRoomColumns";
import {createBasicTableComponent} from "../common/ui/table/ControlRoomTable";
import {connect} from "react-redux";
import {DateUtil} from "../common/util/DateUtil";
import {actions} from "./actions";
import {ImportedData} from "./model";
import {selectors} from "./selectors";
import {selectors as pagedDataSelectors} from "../common/util/pagination/PaginationUtil";
import {sortItems, SortSettings} from "../common/ui/table/model";
import {COLUMN_SORT_SETTINGS_DATA, localRepository} from "../common/LocalRepository";

const config = {
    canMultiSelect: true,
    noItemsFoundMessage: <FormattedMessage id="studio.data.data-list-page.no-items" defaultMessage="No data found."/>,
};

let currentOnSortChange: ((colKey: string) => void) | null = null;

const makeHeader = (label: React.ReactNode, columnKey: string) => {
  let indicator = "";
  const sortSettings: SortSettings = localRepository.getOptions<SortSettings>(COLUMN_SORT_SETTINGS_DATA);
  if (sortSettings?.column === columnKey) {
    indicator = sortSettings.direction.startsWith("asc") ? " ▲" : " ▼";
    if (sortSettings.direction.endsWith("_folderTop")) {
      indicator += " 🗀"
    }
  }

    return (
        <button
            type="button"
            onClick={() => currentOnSortChange && currentOnSortChange(columnKey)}
            style={{background: "none", border: "none", padding: 0, margin: 0, cursor: "pointer"}}
            aria-label={`Sort by ${String(columnKey)}`}
        >
            <div className={"columnLabel"} style={{display: "inline-flex"}}>
                {label}
              <div className={"sortIndicator"} id={`sortIndicator_${columnKey}`}>{indicator}</div>
            </div>
        </button>
    );
}

const columns: ColumnsDescription<ImportedData> = {
    name: {
        header: makeHeader(<FormattedMessage id="studio.data.data-list-page.title" defaultMessage="Title"/>, "name"),
        cellContent: (importedData) => <Link
            to={`/data/${encodeURIComponent(importedData.id)}`}>{importedData.title}</Link>,
        cellTooltip: (importedData) => importedData.title,
        columnWidth: ColumnWidth.flex(2),
    },
    path: {
        header: makeHeader(<FormattedMessage id="studio.data.data-list-page.path" defaultMessage="Path"/>, "path"),
        cellContent(importedData) {
            if (importedData.type.toUpperCase() === "FOLDER") {
                const index = importedData.filePath.toUpperCase().indexOf("FOLDER=");
                const path = (index === 0) ? importedData.filePath.substring(index + 7) : importedData.filePath;
                return (path === importedData.title) ? "" : path;
            }
            return importedData.filePath;
        },
        cellTooltip: (importedData) => importedData.filePath,
        columnWidth: ColumnWidth.flex(5),
    },
    dataFormat: {
        header: makeHeader(<FormattedMessage id="studio.data.data-list-page.format" defaultMessage="Format"/>,
            "dataFormat"),
        cellContent: (importedData) => importedData.type,
        cellTooltip: (importedData) => importedData.type,
        columnWidth: ColumnWidth.pixels(120),
    },
    creationTime: {
        header: makeHeader(<FormattedMessage id="studio.data.data-list-page.date-created"
                                             defaultMessage="Date Created"/>, "creationTime"),
        cellContent: (importedData) => DateUtil.formatDateTime(importedData.creationTime),
        cellTooltip: (importedData) => DateUtil.formatDateTime(importedData.creationTime),
        columnWidth: ColumnWidth.DATETIME_COLUMN_WIDTH,
    },
};

export const ImportedDataListComponent = createBasicTableComponent(columns);

function valueForColumn(item: ImportedData, columnKey?: string | null) {
    switch (columnKey) {
    case "name":
        return (item.title || "").toString().toLowerCase();
    case "path":
        return (item.filePath || "").toString().toLowerCase();
    case "dataFormat":
        return (item.type || "").toString().toLowerCase();
    case "creationTime":
        const t = (item as any).creationTime;
        const num = typeof t === "number" ? t : Date.parse(t);
        return Number.isNaN(num) ? 0 : num;
    default:
        return "";
    }
}

const ConnectedImportedDataList = connect(
    (state: any, ownProps: any) => {
        const pagedState = selectors.getPagedState(state) || ({} as any);
        const rawItems: ImportedData[] = pagedDataSelectors.getAllLoadedData<ImportedData>(pagedState) || [];
      const sorted = sortItems(rawItems, valueForColumn, ownProps?.sortSettings);
        return {
            isPaged: true,
            canMultiSelect: config.canMultiSelect,
            noItemsFoundMessage: config.noItemsFoundMessage,
            data: sorted,
            pageSize: pagedDataSelectors.getPageSize(pagedState),
            pages: pagedDataSelectors.getAllPages<ImportedData>(pagedState),
            isAllDataLoaded: pagedDataSelectors.isAllPagesLoaded(pagedState),
        };
    },
    (dispatch: any) => ({
        loadPage: (pageNumber: number) => dispatch(actions.requestPage(pageNumber)),
    })
)(injectIntl(ImportedDataListComponent));

export const ImportedDataList = (props: any) => {
    currentOnSortChange = props.onSortChange || null;
    return <ConnectedImportedDataList {...props} />;
};